import { FDRStatus, FieldModel } from '@cibo/core'
import {
  PaginatedFieldsContext,
  PaginatedFieldsProvider,
  ReadOnlyPaginatedFieldsTable,
  SelectablePaginatedFieldsTable,
  fieldGrowerColumn,
  fieldIdentifierColumn,
  fieldUpdatedAtColumn,
  useLandManagement,
  usePaginatedFields,
} from '@cibo/landmanager'
import { Loading, useAuth, useWindowTitle } from '@cibo/profile'
import {
  SelectedFieldsProgramLimitProvider,
  fieldOpportunitiesColumn,
  useProgram,
  useProgramEngagements,
  useSelectedFieldsProgramLimit,
} from '@cibo/programs'
import { ListCard } from '@cibo/ui'
import WarningIcon from '@mui/icons-material/Warning'
import { Alert, AlertTitle, Typography } from '@mui/material'
import { GridColDef, GridRowParams } from '@mui/x-data-grid-pro'
import { EngagementImpossible } from 'components/EngagementImpossible'
import { EnrollSelectedFields } from 'components/EnrollSelectedFields'
import { PROGRAM_ROUTES } from 'navigation/types/routes'
import { useFieldTableActionsColumn } from 'pages/LandManager/useFieldTableActionsColumn'
import { useContext, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { Navigate, generatePath, useParams } from 'react-router-dom'
import { ENGAGEMENT_PATHS } from '../../../../../navigation/types/engagementRoutes'
import { FinishEngagementCTA } from '../FinishEngagementCTA'

export const eligibleFieldsBaseQuery = (programId: string) => ({
  fdrStatusAnyOf: [
    { status: FDRStatus.eligibleForEnrollment, programId },
    { status: FDRStatus.futureEnrollment, programId },
  ],
})

export const EligibleFields = () => {
  const { programId = '' } = useParams()

  return (
    <PaginatedFieldsProvider>
      <SelectedFieldsProgramLimitProvider programId={programId}>
        <EligibleFieldsDisplay />
      </SelectedFieldsProgramLimitProvider>
    </PaginatedFieldsProvider>
  )
}

const EligibleFieldsDisplay = () => {
  const { t } = useTranslation('impact/pages/ProgramV2Detail/EligibleFields')
  const { t: tColumns } = useTranslation('@cibo/landmanager/FieldsTable')
  const { persona } = useAuth()
  const { programId = '' } = useParams()
  const program = useProgram({ programId })
  const { selectedRowIds } = useContext(PaginatedFieldsContext)
  const { isReady, display, limits } = useSelectedFieldsProgramLimit()
  useWindowTitle(program.data?.content.fields.title)

  const { openFieldMetaDialog } = useLandManagement()
  const actionsColumn = useFieldTableActionsColumn({
    onEditMeta: resourceId => openFieldMetaDialog({ resourceId: resourceId as string }),
    programId,
  })

  const engagements = useProgramEngagements({ programId: [programId] })

  const baseQuery = useMemo(() => eligibleFieldsBaseQuery(programId), [programId])
  const fields = usePaginatedFields(0, 1, baseQuery)

  const columns = useMemo(() => {
    return [
      actionsColumn,
      fieldIdentifierColumn({ t: tColumns, flex: 3 }),
      fieldOpportunitiesColumn({
        t: tColumns,
        flex: 1,
        programId,
      }),
      persona === 'grower' ? undefined : fieldGrowerColumn({ t: tColumns, width: 150 }),

      fieldUpdatedAtColumn({
        t: tColumns,
        width: 150,
        flex: 0,
      }),
    ].filter(c => !!c) as GridColDef[]
  }, [programId])

  if (!fields.isFetched || !engagements.isFetched || !program.isFetched) {
    return <Loading />
  }

  if (!fields.data?.numAvailable) {
    if (!!engagements.data?.items[0]) {
      return (
        <FinishEngagementCTA
          markdown={t('finishEnrollmentCTAMd', {
            count: engagements.data.items[0].fieldCounts?.total,
          })}
          // @ts-ignore program is loaded
          engageType={program.data.engageType}
          to={generatePath(`${PROGRAM_ROUTES.ENGAGEMENTS}/${ENGAGEMENT_PATHS.ENGAGEMENT}`, {
            programId,
            engagementId: engagements.data.items[0].id,
          })}
        />
      )
    } else {
      return program.data?.enrollmentWindow.enrollmentStatus === 'closed' ? (
        <Navigate to={generatePath(PROGRAM_ROUTES.ABOUT, { programId })} replace />
      ) : (
        <EngagementImpossible programId={programId} />
      )
    }
  }

  const isFuture = program.data?.enrollmentWindow.enrollmentStatus === 'future'

  return (
    <>
      {isReady && display && (
        <Alert severity="warning" icon={<WarningIcon />}>
          <AlertTitle>{t('programNearCapacity')}</AlertTitle>
          {t('selectedExceedCaps', {
            count: selectedRowIds.length,
            context: limits && limits[0].display && limits[0].unit,
          })}
        </Alert>
      )}
      {!!isFuture && (
        <Typography variant="body2" color="text.secondary">
          {t('future')}
        </Typography>
      )}
      <ListCard title={t('programs')} subTitle={t('eligibleFields')}>
        {!!isFuture ? (
          <ReadOnlyPaginatedFieldsTable
            getRowHeight={() => 'auto'}
            columns={columns}
            baseQuery={baseQuery}
          />
        ) : (
          <SelectablePaginatedFieldsTable
            getRowHeight={() => 'auto'}
            columns={columns}
            baseQuery={baseQuery}
            isRowSelectable={(params: GridRowParams<FieldModel>) =>
              params.row.can(['field_enroll'])
            }
            components={{
              Toolbar: () => (
                <Typography variant="caption" color="text.secondary">
                  {t('instructions', { context: program.data?.engageType })}
                </Typography>
              ),
              BulkActions: () => (
                <EnrollSelectedFields
                  buttonProps={{ variant: 'contained', size: 'small' }}
                  programId={programId}
                />
              ),
            }}
          />
        )}
      </ListCard>
    </>
  )
}
